import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Nav } from "../components/Nav";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { IMAGES } from "../data/content";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export function AuthShell({ title, emph, children }) {
  return (
    <div className="min-h-screen bg-[#FAF4E9] flex">
      <Nav />
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={IMAGES.community} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#132619]/70" />
        <div className="absolute bottom-12 left-12 right-12">
          <p className="font-serif-d text-[#FAF4E9] text-4xl leading-tight">Built for <span className="italic-emph text-[#DCBB6C]">more</span> than survival.</p>
          <p className="text-[#FAF4E9]/70 mt-4">Join the community walking from wounded to whole.</p>
        </div>
      </div>
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-32">
        <div className="w-full max-w-sm">
          <h1 className="font-serif-d text-4xl text-[#1E3A2B] tracking-tight">{title} <span className="italic-emph text-[#C0953A]">{emph}</span></h1>
          {children}
          <button data-testid="google-login-btn" onClick={googleLogin}
            className="w-full mt-4 border border-[#21302A]/20 hover:border-[#1E3A2B] transition-colors py-4 uppercase tracking-widest text-xs font-medium text-[#21302A] flex items-center justify-center gap-3">
            Continue with Google
          </button>
        </div>
      </div>
    </div>
  );
}

const field = "bg-transparent border-b border-[#21302A]/20 focus:border-[#1E3A2B] outline-none px-0 py-3 w-full text-[#21302A] transition-colors";

export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { loginWithToken } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      loginWithToken(data.token, data.user);
      toast.success("Welcome back!");
      nav("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Welcome" emph="back.">
      <form onSubmit={submit} className="mt-8 space-y-6" data-testid="login-form">
        <input data-testid="login-email" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required className={field} />
        <input data-testid="login-password" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required className={field} />
        <button data-testid="login-submit" disabled={loading} className="w-full bg-[#1E3A2B] text-[#FAF4E9] hover:bg-[#2E5240] transition-colors py-4 uppercase tracking-widest text-xs font-medium disabled:opacity-50">
          {loading ? "Signing in…" : "Log In →"}
        </button>
      </form>
      <p className="mt-6 text-sm text-[#21302A]/60">New here? <Link to="/signup" className="text-[#C0953A] hover:underline">Create an account</Link></p>
    </AuthShell>
  );
}

export function Signup() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { loginWithToken } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      loginWithToken(data.token, data.user);
      toast.success("Welcome to the overflow!");
      nav("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sign up failed");
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Begin your" emph="overflow.">
      <form onSubmit={submit} className="mt-8 space-y-6" data-testid="signup-form">
        <input data-testid="signup-name" placeholder="Your Name" value={name} onChange={(e) => setName(e.target.value)} required className={field} />
        <input data-testid="signup-email" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required className={field} />
        <input data-testid="signup-password" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className={field} />
        <button data-testid="signup-submit" disabled={loading} className="w-full bg-[#1E3A2B] text-[#FAF4E9] hover:bg-[#2E5240] transition-colors py-4 uppercase tracking-widest text-xs font-medium disabled:opacity-50">
          {loading ? "Creating…" : "Create Account →"}
        </button>
      </form>
      <p className="mt-6 text-sm text-[#21302A]/60">Already a member? <Link to="/login" className="text-[#C0953A] hover:underline">Log in</Link></p>
    </AuthShell>
  );
}
